import {
  BookOpen,
  Sparkles,
  Search,
//   Plus,
  TrendingUp,
  Eye,
  Edit,
  Trash2,
  PlayCircle,
  FileText,
  UsersRound,
  Loader2,
} from 'lucide-react';
import { EducationalContent } from './educationTypes';

interface Props {
  content: EducationalContent[];
  isLoading?: boolean;
  searchTerm?: string;
  onSearchChange?: (value: string) => void;
  onAssign: (content: EducationalContent) => void;
}

export default function GeneralEducationTab({ content, isLoading, searchTerm = '', onSearchChange, onAssign }: Props) {
  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search educational content..."
              value={searchTerm}
              onChange={(e) => onSearchChange?.(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
          <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
            <option value="all">All Types</option>
            <option value="video_script">Videos</option>
            <option value="article">Articles</option>
          </select>
          <select className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500">
            <option value="all">All Categories</option>
            <option value="general">General</option>
            <option value="high_risk">High Risk</option>
            <option value="post_treatment">Post Treatment</option>
            <option value="procedures">Procedures</option>
            <option value="preventive_care">Preventive Care</option>
          </select>
          {/* <button className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
            <Plus className="w-5 h-5" />
            <span>New Content</span>
          </button> */}
        </div>
      </div>

      {/* Content Library Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20 text-gray-400">
          <Loader2 className="w-8 h-8 animate-spin mr-3" />
          <span>Loading content...</span>
        </div>
      ) : content.length === 0 ? (
        <div className="bg-white rounded-lg shadow-lg flex flex-col items-center justify-center py-20 text-gray-400">
          <BookOpen className="w-16 h-16 mb-4 text-gray-200" />
          <p className="text-lg font-medium">No educational content yet</p>
          <p className="text-sm mt-1">Use "Generate AI Content" to create your first piece</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {content.map((item) => (
            <div key={item.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
              {/* Content Preview */}
              <div className="relative h-48 bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center">
                {item.type === 'video' && <PlayCircle className="w-16 h-16 text-white opacity-80" />}
                {item.type === 'article' && <FileText className="w-16 h-16 text-white opacity-80" />}
                {item.aiGenerated && (
                  <div className="absolute top-3 right-3 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-semibold flex items-center space-x-1">
                    <Sparkles className="w-3 h-3" />
                    <span>AI</span>
                  </div>
                )}
                <div className="absolute top-3 left-3 bg-white/90 px-2 py-1 rounded text-xs font-medium capitalize">
                  {item.type}
                </div>
              </div>

              {/* Content Details */}
              <div className="p-4">
                <h3 className="font-bold text-gray-900 text-lg mb-1">{item.title}</h3>
                <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.description}</p>

                {item.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {item.tags.map((tag, index) => (
                      <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <div className="flex items-center space-x-3 text-xs text-gray-500">
                    <span className="flex items-center space-x-1">
                      <Eye className="w-4 h-4" />
                      <span>{item.viewCount}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <TrendingUp className="w-4 h-4" />
                      <span>{item.engagementRate}%</span>
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors">
                      <Eye className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                      <Edit className="w-4 h-4" />
                    </button>
                    <button className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => onAssign(item)}
                  className="w-full mt-3 flex items-center justify-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <UsersRound className="w-4 h-4" />
                  <span>Assign to Patients</span>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
